import * as React from "react";
import { notifyError } from "@authe/ui-core";
import { useSession } from "@authe/ui-core/session";
import { useCurrentOrg } from "@authe/ui-core/tenant";
import { useTenancySource } from "@/api/adapters";

export const IMPERSONATED_SWITCH_HINT =
  "Switching needs a fresh sign-in, which an impersonated session cannot do.";

/** Persist the selection before re-authentication, consistently in every surface. */
export function useTenantSwitch() {
  const { manager, session } = useSession();
  const { tenantId: current } = useCurrentOrg();
  const source = useTenancySource();
  const impersonating = Boolean(session?.claims?.impersonated);
  const pending = React.useRef(false);
  const [busy, setBusy] = React.useState(false);

  const switchTo = React.useCallback(async (tenantId: string) => {
    if (tenantId === current || pending.current || impersonating) return;
    pending.current = true;
    setBusy(true);
    let switched = false;
    try {
      await source.setActiveTenant(tenantId);
      switched = true;
      await manager.login({ returnTo: window.location.href, identityChange: "tenant-switch" });
    } catch (error) {
      notifyError(error, {
        title: switched
          ? "Switched, but this tab could not reload — sign in again to land in the new tenant."
          : "Couldn’t switch tenant — you are still in the current one.",
      });
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }, [current, impersonating, manager, source]);

  return { switchTo, busy, impersonating };
}
